import { useQuery } from '@tanstack/react-query'
import { useUser } from '@clerk/clerk-react'
import { getMyEnablerSubmission } from '@/services/enablers'
import type { EnablerSubmission } from '@/lib/supabase'

/**
 * Hook to check enabler submission status for the current user
 * Uses TanStack Query for caching and automatic refetching
 *
 * @returns {Object} Submission status and computed booleans
 * - submission: Full EnablerSubmission object or null
 * - isLoading: True while loading or checking auth
 * - hasSubmitted: True if user has submitted the form (any status)
 * - isApproved: True if submission status is 'approved'
 * - isPending: True if submission status is 'pending'
 * - isRejected: True if submission status is 'rejected'
 */
export function useEnablerSubmission() {
  const { isSignedIn, isLoaded } = useUser()

  const { data: submission, isLoading: isQueryLoading } = useQuery<EnablerSubmission | null>({
    queryKey: ['enabler-submission', isSignedIn],
    queryFn: () => getMyEnablerSubmission(),
    enabled: isLoaded && isSignedIn,
  })

  const isLoading = !isLoaded || isQueryLoading

  return {
    submission,
    isLoading,
    hasSubmitted: Boolean(submission),
    isApproved: submission?.submission_status === 'approved',
    isPending: submission?.submission_status === 'pending',
    isRejected: submission?.submission_status === 'rejected',
  }
}
